import { QuizFlow } from '@naturalcycles/shared'
import { DefaultQuizSection, QuizSections } from '@src/typings/quiz.enum.js'
import { AppQuizFlow, QuizSignatureObject } from '@src/typings/quiz.model.js'
import { allQuizSections } from '../cnst/quiz/quiz.cnst.js'
import { QuizPage } from '../cnst/quiz/quiz-pages.cnst.js'
import { tr, trIfExists } from '../srv/translation.util.js'

export const trWithFallback = (key: string, fallback: string): string => {
  return trIfExists(key) || tr(fallback)
}

export const addPageToFlowAfterCurrent = (
  flow: QuizFlow<QuizPage>,
  section: DefaultQuizSection,
  currentPage: QuizPage,
  pageToAdd: QuizPage,
): QuizFlow<QuizPage> => {
  const updatedSection = [...(flow[allQuizSections[section]] || [])]
  const currentIndex = updatedSection.indexOf(currentPage)
  if (currentIndex !== -1 && !updatedSection.includes(pageToAdd)) {
    updatedSection.splice(currentIndex + 1, 0, pageToAdd)
  }
  flow[allQuizSections[section]] = updatedSection
  return flow
}

export const addPagesToFlowAfterCurrent = (
  flow: QuizFlow<QuizPage>,
  section: DefaultQuizSection,
  currentPage: QuizPage,
  pagesToAdd: QuizPage[],
): QuizFlow<QuizPage> => {
  const updatedSection = [...(flow[allQuizSections[section]] || [])]
  const currentIndex = updatedSection.indexOf(currentPage)

  if (currentIndex !== -1) {
    const filteredPagesToAdd = pagesToAdd.filter(page => !updatedSection.includes(page))

    updatedSection.splice(currentIndex + 1, 0, ...filteredPagesToAdd)
  }

  flow[allQuizSections[section]] = updatedSection
  return flow
}

export const addPageToFlowAtEndOfSection = (
  flow: QuizFlow<QuizPage>,
  section: DefaultQuizSection,
  pageToAdd: QuizPage,
): QuizFlow<QuizPage> => {
  const updatedSection = [...(flow[allQuizSections[section]] || [])]
  const alreadyAdded = updatedSection.includes(pageToAdd)
  if (alreadyAdded) return flow
  updatedSection.push(pageToAdd)
  flow[allQuizSections[section]] = updatedSection
  return flow
}

export const removePagesFromFlow = (
  flow: QuizFlow<QuizPage>,
  section: DefaultQuizSection,
  pagesToRemove: QuizPage[],
): QuizFlow<QuizPage> => {
  let updatedFlow = flow
  pagesToRemove.forEach(pageToRemove => {
    updatedFlow = removePageFromFlow(updatedFlow, section, pageToRemove)
  })
  return flow
}

export const removePageFromFlow = (
  flow: QuizFlow<QuizPage>,
  section: DefaultQuizSection,
  pageToRemove: QuizPage,
): QuizFlow<QuizPage> => {
  const updatedSection = [...(flow[allQuizSections[section]] || [])]
  const currentIndex = updatedSection.indexOf(pageToRemove)
  if (currentIndex === -1) return flow

  updatedSection.splice(currentIndex, 1)
  flow[allQuizSections[section]] = updatedSection
  return flow
}

export const clearFlowAfterCurrentSection = (
  flow: QuizFlow<QuizPage>,
  currentSection: DefaultQuizSection,
): QuizFlow<QuizPage> => {
  const updated = [...flow].slice(0, allQuizSections[currentSection] + 1)
  return updated
}

export abstract class QuizModifier<QUIZ_ENGINE_CONFIG> {
  abstract _shouldEnhance(obj: QuizSignatureObject): boolean
  abstract enhanceEngineConfig(
    obj: QuizSignatureObject,
    config?: QUIZ_ENGINE_CONFIG,
  ): QUIZ_ENGINE_CONFIG
  abstract enhanceSections(obj: QuizSignatureObject, sections?: QuizSections): QuizSections
  abstract enhanceInitialFlow(obj: QuizSignatureObject, flow?: AppQuizFlow): AppQuizFlow
  abstract enhanceEntry(obj: QuizSignatureObject, entry?: string | null): string | null
  abstract enhanceExit(obj: QuizSignatureObject, exit?: string | null): string | null
}
